<template>
    <div class="precedent-case-editor">
        <!-- 파싱 불가(모델이 깨진 형식을 냄) → 통째로 편집하는 폴백. 케이스 분해가
             안 되는 상태에서 카드로 쪼개는 척하면 오히려 혼란스럽다. -->
        <template v-if="parseError">
            <b-alert show variant="warning" class="small py-2">
                결과 형식을 판례 단위로 나눠 읽을 수 없어 전체 내용을 그대로 보여줍니다.
                [다시 만들기]를 권장합니다.
            </b-alert>
            <b-form-textarea :value="value" rows="18" class="raw-fallback"
                             @input="$emit('input', $event)"/>
        </template>

        <template v-else>
            <div class="small text-muted mb-2">
                판례 {{ cases.length }}건.
                <span v-if="changedKeys.size">
                    이번 피드백으로 <strong>{{ changedKeys.size }}건</strong>이 바뀌었습니다 (위에 표시).
                </span>
                <span v-else>이번 피드백은 기존 판례로 충분해 새로 바뀐 판례가 없습니다.</span>
            </div>

            <b-list-group class="case-list">
                <b-list-group-item
                    v-for="c in displayCases"
                    :key="c.key"
                    class="px-2 py-2"
                    :variant="c.changed ? 'success' : ''"
                >
                    <div class="d-flex align-items-center">
                        <b-badge v-if="c.changed" :variant="changeVariant(c.key)" class="mr-2">
                            {{ changeLabel(c.key) }}
                        </b-badge>
                        <b-badge v-if="c.parsed" :variant="statusVariant(c.parsed.confirmed_status)"
                                 class="mr-2">
                            {{ judgementLabel(c.parsed.confirmed_status) }}
                        </b-badge>
                        <b-badge v-else variant="danger" class="mr-2">형식 오류</b-badge>
                        <span class="case-scenario text-truncate mr-auto">
                            {{ c.parsed ? (c.parsed.scenario || '(시나리오 없음)') : c.raw }}
                        </span>
                        <b-button size="sm" variant="link" class="p-0"
                                  @click="toggle(c.key)">
                            {{ open[c.key] ? '접기' : (c.dirty ? '수정됨 · 펼치기' : '자세히') }}
                        </b-button>
                    </div>

                    <b-collapse :visible="!!open[c.key]" class="mt-2">
                        <template v-if="c.parsed">
                            <dl class="row mb-1 small case-fields">
                                <dt class="col-3 text-muted">판정</dt>
                                <dd class="col-9">{{ judgementLabel(c.parsed.confirmed_status) }}</dd>
                                <dt class="col-3 text-muted">근거</dt>
                                <dd class="col-9">{{ c.parsed.reason || '-' }}</dd>
                                <template v-if="c.parsed.feedback">
                                    <dt class="col-3 text-muted">발송 문구</dt>
                                    <dd class="col-9">{{ c.parsed.feedback }}</dd>
                                </template>
                            </dl>
                        </template>

                        <b-button v-b-toggle="'raw-' + c.key" variant="link" size="sm"
                                  class="p-0 text-muted">
                            원본 편집
                        </b-button>
                        <b-collapse :id="'raw-' + c.key">
                            <div class="small text-muted mt-1 mb-1">
                                이 판례 한 건의 원본입니다. 형식이 정해져 있어 잘못 고치면 저장이 거부됩니다.
                            </div>
                            <b-form-textarea :value="c.raw" rows="5" class="raw-line"
                                             @input="updateCase(c.key, $event)"/>
                            <div v-if="c.error" class="small text-danger mt-1">
                                형식 오류: {{ c.error }}
                            </div>
                        </b-collapse>
                    </b-collapse>
                </b-list-group-item>
            </b-list-group>
        </template>
    </div>
</template>

<script>
import {judgementLabel} from './prompt-labels';

// jsonl 한 줄 = 판례 하나. 안정적인 key 가 필요해 case_id 를 쓰되, 없거나 겹치면
// 라인 인덱스를 붙인다(편집 중 case_id 를 지워도 카드가 튀지 않게).
function parseCases(text) {
    const lines = (text || '').split('\n');
    const cases = [];
    let anyParsed = false;
    lines.forEach((line, idx) => {
        const raw = line;
        if (!raw.trim()) return;   // 빈 줄은 버린다(직렬화 시 재생성)
        let parsed = null;
        let error = null;
        try {
            const obj = JSON.parse(raw);
            if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
                parsed = obj;
                anyParsed = true;
            } else {
                error = '객체가 아닙니다';
            }
        } catch (e) {
            error = e.message;
        }
        const cid = parsed && parsed.case_id ? String(parsed.case_id) : null;
        cases.push({key: cid ? `${cid}#${idx}` : `line#${idx}`, caseId: cid, raw, parsed, error});
    });
    return {cases, anyParsed};
}

export default {
    name: 'PrecedentCaseEditor',
    props: {
        // v-model — 전체 jsonl 문자열. 이 컴포넌트는 이걸 파싱/편집/재조립만 한다.
        value: {type: String, default: ''},
        // apply-preview 의 dispositions — 이번에 바뀐 판례(add/absorb)를 강조하는 데 쓴다.
        dispositions: {type: Array, default: () => []},
    },
    data() {
        return {open: {}};
    },
    computed: {
        parsed() {
            return parseCases(this.value);
        },
        cases() {
            return this.parsed.cases;
        },
        // 한 줄도 파싱 안 되면 케이스 편집이 무의미 → 폴백.
        parseError() {
            return this.cases.length > 0 && !this.parsed.anyParsed;
        },
        // 이번 피드백이 실제로 바꾼 판례(add=신규, absorb=보강). skip 은 무변경이라 제외.
        changeMap() {
            const m = {};
            (this.dispositions || []).forEach((d) => {
                if (!d || !d.target_case_id) return;
                if (d.action === 'add' || d.action === 'absorb') {
                    m[String(d.target_case_id)] = d.action;
                }
            });
            return m;
        },
        changedKeys() {
            const s = new Set();
            this.cases.forEach((c) => {
                if (c.caseId && this.changeMap[c.caseId]) s.add(c.key);
            });
            return s;
        },
        displayCases() {
            // 바뀐 판례를 위로. 저장 순서(원래 라인 순서)와는 무관 — serialize 는
            // this.cases(원래 순서)를 쓴다. 최소 diff 를 지키기 위한 분리다.
            const marked = this.cases.map((c) => ({
                ...c,
                changed: this.changedKeys.has(c.key),
                dirty: !!this.open[c.key],   // 자리표시자 — 실제 dirty 는 아래 갱신에서
            }));
            return [...marked].sort((a, b) => (b.changed ? 1 : 0) - (a.changed ? 1 : 0));
        },
    },
    watch: {
        // 새 결과가 들어오면(다시 만들기) 바뀐 판례는 자동으로 펼쳐 눈에 띄게 한다.
        value: {
            immediate: true,
            handler() {
                const next = {};
                this.cases.forEach((c) => {
                    if (this.changedKeys.has(c.key)) next[c.key] = true;
                });
                this.open = next;
            },
        },
    },
    methods: {
        judgementLabel,
        toggle(key) {
            this.$set(this.open, key, !this.open[key]);
        },
        changeLabel(key) {
            const c = this.cases.find((x) => x.key === key);
            const action = c && c.caseId ? this.changeMap[c.caseId] : null;
            return action === 'add' ? '이번에 추가' : '이번에 보강';
        },
        changeVariant(key) {
            const c = this.cases.find((x) => x.key === key);
            const action = c && c.caseId ? this.changeMap[c.caseId] : null;
            return action === 'add' ? 'success' : 'info';
        },
        statusVariant(status) {
            return {
                NO_SUPPLEMENT: 'success',
                SUPPLEMENT_REQUIRED: 'warning',
                REVIEW_REQUIRED: 'info',
                PROCESSING_FAILED: 'danger',
            }[status] || 'secondary';
        },
        // 한 판례의 원본을 고치면 그 줄만 바꿔 전체를 재조립해 올린다. 나머지 줄은
        // 손대지 않는다(최소 diff). 저장 순서는 this.cases 의 원래 라인 순서를 지킨다.
        updateCase(key, newRaw) {
            const serialized = this.cases
                .map((c) => (c.key === key ? newRaw : c.raw))
                .join('\n');
            this.$emit('input', `${serialized}\n`);
        },
    },
};
</script>

<style scoped>
.case-list {
    max-height: 420px;
    overflow-y: auto;
}
.case-scenario {
    max-width: 60%;
    font-size: 0.85rem;
}
.case-fields dd {
    white-space: pre-wrap;
    word-break: break-word;
}
.raw-line,
.raw-fallback {
    font-family: Consolas, Monaco, 'Courier New', monospace;
    font-size: 0.8rem;
}
</style>
